/**
 * PlayerFrame — R232q21 Phase 2 (Sirus 1:1 UI rebuild).
 *
 * Top-left WoW 3.3.5a / Sirus self-frame. Renders:
 *   – 64px square portrait, gold border, race-tinted backdrop
 *   – Stacked HP + power bars (resource colour drives the lower bar)
 *   – Name label (Cinzel display) with small gold level pill + class-icon mini
 *   – PvP swords flag and group/raid leader crown placeholders
 *   – `<SirusFrame size="md" glow={inCombat}>` chrome wrapping the lot
 *
 * Data sources:
 *   – `useNet().selfId` + `players[selfId]` for live HP / mana / level / class
 *   – `useUI().draft.classId` / `.draft.raceId` as the creator-time fallback
 *     so the frame renders something sensible before the first Colyseus
 *     state delta lands (race tint + resource colour stay correct).
 *
 * Position: fixed top-left (16,16). z-index 30 — under the Settings modal
 * (z 50) but above the action bar / hotbars / chat (≤ 25).
 *
 * Mobile: `@media (pointer: coarse)` scales the frame to 0.85×, a touch
 * tighter — the rest of the responsive HUD is owned by the parent layout.
 */

import { memo, useMemo } from 'react';
import { useNet, useSelfPlayer, type RemotePlayer } from '../../state/net-store.js';
import { useUI } from '../../state/store.js';
import { RACES, type RaceId } from '../../data/wow-races.js';
import { CLASSES, classById, type ClassId } from '../../data/classes.js';

/** Resource bar token name → CSS variable. Matches sirus-tokens.css. */
type ResourceKey = 'mana' | 'rage' | 'focus' | 'energy' | 'runic' | 'faith';

const RESOURCE_TOKEN: Record<ResourceKey, string> = {
  mana: 'var(--aiwari-bar-mana)',
  rage: 'var(--aiwari-bar-rage)',
  focus: 'var(--aiwari-bar-focus)',
  energy: 'var(--aiwari-bar-energy)',
  runic: 'var(--aiwari-bar-runic)',
  // No dedicated faith token in the Phase-1 palette — Paladin shares mana blue.
  faith: 'var(--aiwari-bar-mana)',
};

/**
 * Race-tinted hex backdrop for the portrait. Falls back to the gold-dark
 * stone tone when the race id is unknown (e.g. world-load before draft has
 * synced). Pulled directly from RACES[].colour to stay in lock-step with the
 * RealmSelect / CharacterCreator palettes.
 */
function getRaceTint(raceId: RaceId | string | null | undefined): string {
  if (!raceId) return '#5C4724';
  const found = RACES.find((r) => r.id === raceId);
  return found?.colour ?? '#5C4724';
}

/**
 * Class → bar resource. Centralised so PowerBar, Cast bar and HUD stat
 * panels all agree on which colour token to render. `'energy'` is included
 * for forward-compat with future Rogue / Druid (cat) shifts even though the
 * current ClassDef.resource union doesn't list it yet.
 */
function getResourceFor(classId: ClassId | string | null | undefined): ResourceKey {
  if (!classId) return 'mana';
  const cls = classById(classId as ClassId);
  if (!cls) return 'mana';
  // ClassDef.resource is a narrower union; cast through unknown so the
  // `energy` branch type-checks if we ever add it server-side.
  return cls.resource as ResourceKey;
}

/** Format a current/max pair for the bar text overlay. '-' if unknown. */
function fmtPair(curr: number | undefined, max: number | undefined): string {
  if (typeof curr !== 'number' || typeof max !== 'number') return '-';
  return `${Math.max(0, Math.round(curr))}/${Math.max(0, Math.round(max))}`;
}

/**
 * 16px class-icon placeholder. We deliberately don't load a sprite sheet
 * yet — that's a job for the icon-pack pass. For now we render a coloured
 * disc with the first glyph of the Russian class name; matches the existing
 * spellbook icon fallback. Falls back to the gold token if class is unknown.
 */
function ClassIconMini({ classId }: { classId: ClassId | string | null | undefined }) {
  const cls = classId ? classById(classId as ClassId) : undefined;
  const colour = cls?.colour ?? 'var(--aiwari-gold)';
  const glyph = cls?.name.ru.charAt(0) ?? '?';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: 3,
        background: colour,
        border: '1px solid var(--aiwari-frame-edge)',
        fontFamily: 'var(--aiwari-font-display)',
        fontSize: 10,
        fontWeight: 700,
        color: '#0F0900',
        lineHeight: 1,
      }}
    >
      {glyph}
    </span>
  );
}

/** Small swords glyph for the PvP-flagged badge. Pure SVG, no image dep. */
function PvpSwords() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ display: 'block' }}
    >
      <g fill="none" stroke="var(--aiwari-bar-hp)" strokeWidth="2" strokeLinecap="round">
        <path d="M3 21 L13 11" />
        <path d="M21 21 L11 11" />
        <path d="M16 4 L20 8" />
        <path d="M8 4 L4 8" />
      </g>
    </svg>
  );
}

/** Five-pointed crown for raid/party leader. */
function LeaderCrown() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ display: 'block' }}
    >
      <path
        d="M3 18 L4 8 L9 13 L12 6 L15 13 L20 8 L21 18 Z"
        fill="var(--aiwari-gold)"
        stroke="var(--aiwari-gold-dark)"
        strokeWidth="1"
      />
    </svg>
  );
}

interface BarProps {
  pct: number;
  fill: string;
  text: string;
  height?: string;
}

function Bar({ pct, fill, text, height = 'var(--aiwari-bar-h)' }: BarProps) {
  return (
    <div className="aiwari-bar aiwari-bar-3d" style={{ height }}>
      <div
        className="aiwari-bar-fill"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: fill }}
      />
      <span className="aiwari-bar-text" style={{ fontSize: 'var(--aiwari-text-sm)' }}>{text}</span>
    </div>
  );
}

export interface PlayerFrameProps {
  /** Visual override for unit tests / Storybook. Production reads useNet. */
  override?: Partial<RemotePlayer>;
  /** TODO: bind to PvP store once flag replication lands. */
  pvpFlagged?: boolean;
  /** TODO: bind to group-store leader id once party state is wired. */
  isGroupLeader?: boolean;
}

function PlayerFrameImpl({
  override,
  pvpFlagged = false,
  isGroupLeader = false,
}: PlayerFrameProps) {
  const selfId = useNet((s) => s.selfId);
  // R232q86zdb (Wave 2 #1): use narrow `useSelfPlayer()` selector instead of
  // `useNet((s) => s.players)` so this frame only re-renders when the self
  // player's own row changes — not every time any of the 60 other players in
  // the zone moves a pixel.
  const selfPlayer = useSelfPlayer();
  const draftClassId = useUI((s) => s.draft.classId);
  const draftRaceId = useUI((s) => s.draft.raceId);
  const draftName = useUI((s) => s.draft.name);

  // Live row wins; fall back to the creator draft so the frame renders
  // immediately even before the first Colyseus state-sync packet arrives.
  const live = useMemo<Partial<RemotePlayer>>(
    () => ({ ...selfPlayer, ...override }),
    [selfPlayer, override],
  );

  const classId = (live.classId ?? draftClassId ?? null) as ClassId | null;
  const raceId = (draftRaceId ?? null) as RaceId | null;
  const name = live.name ?? draftName ?? '-';
  const level = typeof live.level === 'number' ? live.level : null;
  const hp = typeof live.hp === 'number' ? live.hp : undefined;
  const hpMax = typeof live.hpMax === 'number' ? live.hpMax : undefined;
  const res = typeof live.resource === 'number' ? live.resource : undefined;
  const resMax = typeof live.resourceMax === 'number' ? live.resourceMax : undefined;

  const hpPct = hp !== undefined && hpMax && hpMax > 0 ? (hp / hpMax) * 100 : 0;
  const resPct = res !== undefined && resMax && resMax > 0 ? (res / resMax) * 100 : 0;

  const resourceKey = getResourceFor(classId);
  const tint = getRaceTint(raceId);

  // R232q21: WoW "in combat" glow — until a combat-store ships, treat any
  // HP-deficit as combat. Cheap heuristic, replaced once the server pipes
  // an `inCombat` flag through PlayerState.
  const inCombat = hp !== undefined && hpMax !== undefined && hp < hpMax;

  // Hidden until either a draft class or a live row exists, otherwise the
  // empty frame would clutter the auth/realm-select screens.
  if (!selfId && !draftClassId) return null;

  const cls = classId ? CLASSES.find((c) => c.id === classId) : undefined;

  return (
    <div
      data-aiwari-player-frame
      className="aiwari-filigree-frame"
      style={{
        position: 'fixed',
        top: 'var(--aiwari-padding)',
        left: 'var(--aiwari-padding)',
        zIndex: 30,
        // R232q84: rubber sizing — clamp keeps frame between 180px (320px phone)
        // and 280px (4K). 24vmin = 24% of smaller viewport edge (~260px on 1080p).
        width: 'clamp(180px, 24vmin, 280px)',
        padding: 'var(--aiwari-padding)',
        boxSizing: 'border-box',
        // R232q86zcj: borderColor red on combat to indicate hostile state
        borderColor: inCombat ? 'var(--aiwari-bar-hp)' : undefined,
        boxShadow: inCombat
          ? '0 0 12px hsl(0 60% 38% / 0.55), inset 0 1px 0 hsl(0 0% 100% / 0.10)'
          : undefined,
      }}
    >
      <span className="aiwari-filigree-bl" />
      <span className="aiwari-filigree-br" />
      <div style={{ display: 'flex', gap: 'var(--aiwari-gap)', alignItems: 'stretch' }}>
        {/* Portrait — Sirus ROUND class-coloured ring, race-tinted inner,
         *  leader crown TL, level BL (matches user's Sirus reference 1:1). */}
        <div
          className="aiwari-portrait-frame"
          style={{
            flex: '0 0 var(--aiwari-portrait)',
            ['--aiwari-portrait-ring' as string]: cls?.colour ?? 'hsl(44 80% 24%)',
          }}
          aria-label={cls ? `Портрет: ${cls.name.ru}` : 'Портрет'}
        >
          <div
            className="aiwari-portrait-clip"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${tint} 0%, hsl(36 25% 5%) 100%)`,
            }}
          >
            {/* Class glyph centred — placeholder until icon-pack lands */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--aiwari-font-display)',
                fontSize: 'var(--aiwari-text-display)',
                fontWeight: 700,
                color: 'var(--aiwari-gold-soft)',
                textShadow: '0 1px 2px #000, 0 0 4px hsl(0 0% 0% / 0.6)',
                opacity: 0.92,
              }}
            >
              {cls?.name.ru?.[0] ?? '?'}
            </div>
          </div>
          {/* Level pill — engraved gold disc, bottom-left */}
          <div
            className="aiwari-level-pill"
            aria-label="Уровень"
            style={{ position: 'absolute', left: -6, bottom: -6 }}
          >
            {level ?? '-'}
          </div>
          {/* Leader crown top-left */}
          {isGroupLeader && (
            <div style={{ position: 'absolute', left: -4, top: -4 }}>
              <LeaderCrown />
            </div>
          )}
        </div>

        {/* Right column: name on top, HP bar, MP bar */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--aiwari-gap)',
            minWidth: 0,
            paddingTop: 2,
          }}
        >
          {/* Name + flags */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--aiwari-gap)',
              minWidth: 0,
              minHeight: 'var(--aiwari-text-md)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--aiwari-font-display)',
                fontSize: 'var(--aiwari-text-md)',
                fontWeight: 600,
                color: 'var(--aiwari-fg)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: '1 1 auto',
                lineHeight: 1,
              }}
              title={name}
            >
              {name}
            </span>
            <ClassIconMini classId={classId} />
            {pvpFlagged && (
              <span aria-label="Метка PvP" style={{ flex: '0 0 auto' }} title="PvP">
                <PvpSwords />
              </span>
            )}
          </div>
          {/* HP bar */}
          <Bar
            pct={hpPct}
            fill="var(--aiwari-bar-hp)"
            text={fmtPair(hp, hpMax)}
          />
          {/* MP bar */}
          <Bar
            pct={resPct}
            fill={RESOURCE_TOKEN[resourceKey]}
            text={fmtPair(res, resMax)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper.
 *
 * PlayerFrame only ever takes 3 optional props (override / pvpFlagged /
 * isGroupLeader). All other re-renders are driven by zustand subscriptions
 * inside the impl; once those subscriptions are narrowed (useSelfPlayer)
 * the impl only re-renders when self HP/MP/level/class actually changes,
 * NOT on every 15Hz pos patch for other players.
 *
 * The custom `arePropsEqual` returns true when none of the optional
 * inputs changed by-reference / by-value so a parent re-render alone
 * (e.g. SirusHud's `useUI` stage tick) doesn't churn this subtree.
 */
export const PlayerFrame = memo(
  PlayerFrameImpl,
  (a, b) =>
    a.override === b.override &&
    a.pvpFlagged === b.pvpFlagged &&
    a.isGroupLeader === b.isGroupLeader,
);
